import * as THREE from 'three';
import { RoundedBoxGeometry } from 'three/addons/geometries/RoundedBoxGeometry.js';

const BOX_WIDTH = 4.75;
const BOX_HEIGHT = 4.72;
const BOX_DEPTH = 0.34;
const BOX_LID_OPEN_ANGLE = THREE.MathUtils.degToRad(112);

const CARTRIDGE_WIDTH = 2.8;
const CARTRIDGE_HEIGHT = 1.85;
const CARTRIDGE_DEPTH = 0.28;

const publicAsset = (path: string): string => `${import.meta.env.BASE_URL}${path}`;

export type CartridgeInteractionState = 'idle' | 'hovered' | 'selected' | 'inserted';

export interface BoxColliderDescriptor {
  id: string;
  node: THREE.Object3D;
  type: 'box' | 'cylinder';
  size: THREE.Vector3;
  offset: THREE.Vector3;
  isTrigger: boolean;
}

export interface GamePackageAssetUrls {
  boxArtwork: string;
  cartridgeLabel: string;
}

export interface GameBoxModel {
  root: THREE.Group;
  basePivot: THREE.Group;
  lidPivot: THREE.Group;
  innerTray: THREE.Group;
  hingeSocket: THREE.Object3D;
  cartridgeCradleSocket: THREE.Object3D;
  cartridgeLandingSocket: THREE.Object3D;
  colliders: BoxColliderDescriptor[];
  openAngle: number;
  ready: Promise<void>;
  setOpenProgress(progress: number): void;
  getOpenProgress(): number;
  setHover(hovered: boolean): void;
}

export interface CartridgeModel {
  root: THREE.Group;
  frontShell: THREE.Group;
  rearShell: THREE.Group;
  pickupSocket: THREE.Object3D;
  insertionLeadSocket: THREE.Object3D;
  alignmentSocket: THREE.Object3D;
  contactEdgeSocket: THREE.Object3D;
  insertionAxis: THREE.Vector3;
  colliders: BoxColliderDescriptor[];
  ready: Promise<void>;
  setInteractionState(state: CartridgeInteractionState): void;
  getInteractionState(): CartridgeInteractionState;
}

export interface GamePackageModel {
  root: THREE.Group;
  box: GameBoxModel;
  cartridge: CartridgeModel;
  ready: Promise<void>;
  resetCartridgeToCradle(): void;
  setCartridgeRevealProgress(progress: number): void;
  detachCartridgeTo(parent: THREE.Object3D): void;
  dockCartridgeAt(socket: THREE.Object3D): void;
}

export interface GamePackageOptions {
  assetUrls?: Partial<GamePackageAssetUrls>;
  textureAnisotropy?: number;
}

const DEFAULT_ASSET_URLS: GamePackageAssetUrls = {
  boxArtwork: publicAsset('assets/models/box-front-reference.png'),
  cartridgeLabel: publicAsset('assets/models/cartridge-label-reference.png'),
};

type TexturedMaterial = THREE.MeshStandardMaterial | THREE.MeshPhysicalMaterial;

function clamp01(value: number): number {
  return THREE.MathUtils.clamp(value, 0, 1);
}

function easeInOut(value: number): number {
  const t = clamp01(value);
  return t * t * (3 - 2 * t);
}

function makeSocket(
  name: string,
  position: THREE.Vector3,
  rotation = new THREE.Euler(),
): THREE.Object3D {
  const socket = new THREE.Object3D();
  socket.name = name;
  socket.position.copy(position);
  socket.rotation.copy(rotation);
  socket.userData.socket = true;
  return socket;
}

function loadColorTexture(
  loader: THREE.TextureLoader,
  url: string,
  material: TexturedMaterial,
  anisotropy: number,
): Promise<void> {
  return loader
    .loadAsync(url)
    .then((texture) => {
      texture.name = url.split('/').pop() ?? 'reference-texture';
      texture.colorSpace = THREE.SRGBColorSpace;
      texture.wrapS = THREE.ClampToEdgeWrapping;
      texture.wrapT = THREE.ClampToEdgeWrapping;
      texture.minFilter = THREE.LinearMipmapLinearFilter;
      texture.magFilter = THREE.LinearFilter;
      texture.anisotropy = anisotropy;
      material.map = texture;
      material.needsUpdate = true;
    })
    .catch(() => undefined);
}

function makeCylinderBetween(
  start: THREE.Vector3,
  end: THREE.Vector3,
  radius: number,
  material: THREE.Material,
  radialSegments = 12,
): THREE.Mesh {
  const direction = end.clone().sub(start);
  const length = direction.length();
  const mesh = new THREE.Mesh(
    new THREE.CylinderGeometry(radius, radius, length, radialSegments, 1, false),
    material,
  );
  mesh.position.copy(start).add(end).multiplyScalar(0.5);
  mesh.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), direction.normalize());
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
}

function makeBoxFilmScratches(width: number, height: number): THREE.LineSegments {
  const segments: number[] = [];
  const add = (x1: number, y1: number, x2: number, y2: number): void => {
    segments.push(x1, y1, 0, x2, y2, 0);
  };

  add(-width * 0.48, height * 0.43, -width * 0.38, height * 0.47);
  add(-width * 0.46, height * 0.35, -width * 0.42, height * 0.12);
  add(width * 0.32, height * 0.46, width * 0.46, height * 0.38);
  add(width * 0.47, height * 0.22, width * 0.43, -height * 0.08);
  add(-width * 0.47, -height * 0.32, -width * 0.34, -height * 0.46);
  add(-width * 0.29, -height * 0.47, -width * 0.08, -height * 0.45);
  add(width * 0.11, -height * 0.46, width * 0.21, -height * 0.41);
  add(width * 0.35, -height * 0.45, width * 0.47, -height * 0.34);

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(segments, 3));
  const material = new THREE.LineBasicMaterial({
    color: 0xffffff,
    transparent: true,
    opacity: 0.2,
    depthWrite: false,
  });
  const scratches = new THREE.LineSegments(geometry, material);
  scratches.name = 'box-cover-film-scratches';
  scratches.renderOrder = 5;
  return scratches;
}

function makePerimeterRim(
  width: number,
  height: number,
  depth: number,
  thickness: number,
  material: THREE.Material,
): THREE.Group {
  const rim = new THREE.Group();
  rim.name = 'box-inner-tray-rim';
  const horizontalGeometry = new RoundedBoxGeometry(width, thickness, depth, 3, thickness * 0.35);
  const verticalGeometry = new RoundedBoxGeometry(thickness, height, depth, 3, thickness * 0.35);

  for (const y of [-height * 0.5, height * 0.5]) {
    const rail = new THREE.Mesh(horizontalGeometry, material);
    rail.position.y = y;
    rail.castShadow = true;
    rail.receiveShadow = true;
    rim.add(rail);
  }
  for (const x of [-width * 0.5, width * 0.5]) {
    const rail = new THREE.Mesh(verticalGeometry, material);
    rail.position.x = x;
    rail.castShadow = true;
    rail.receiveShadow = true;
    rim.add(rail);
  }
  return rim;
}

function createGameBox(
  assetUrls: GamePackageAssetUrls,
  textureAnisotropy: number,
): GameBoxModel {
  const root = new THREE.Group();
  root.name = 'game-box-root';
  root.userData.interactionRole = 'game-box';

  const shellMaterial = new THREE.MeshPhysicalMaterial({
    color: 0x202126,
    roughness: 0.68,
    metalness: 0,
    clearcoat: 0.08,
    clearcoatRoughness: 0.4,
  });
  shellMaterial.name = 'box-shell-plastic';

  const shellEdgeMaterial = new THREE.MeshStandardMaterial({
    color: 0x09090b,
    roughness: 0.78,
    metalness: 0,
  });
  shellEdgeMaterial.name = 'box-shell-seam';

  const trayMaterial = new THREE.MeshStandardMaterial({
    color: 0x303138,
    roughness: 0.82,
    metalness: 0,
  });
  trayMaterial.name = 'box-inner-tray-plastic';

  const artMaterial = new THREE.MeshPhysicalMaterial({
    color: 0xffffff,
    roughness: 0.55,
    metalness: 0,
    clearcoat: 0.16,
    clearcoatRoughness: 0.34,
  });
  artMaterial.name = 'box-reference-artwork';

  const basePivot = new THREE.Group();
  basePivot.name = 'box-base-pivot';
  root.add(basePivot);

  const baseBody = new THREE.Mesh(
    new RoundedBoxGeometry(BOX_WIDTH, BOX_HEIGHT, BOX_DEPTH, 5, 0.1),
    shellMaterial,
  );
  baseBody.name = 'box-base-shell';
  baseBody.castShadow = true;
  baseBody.receiveShadow = true;
  basePivot.add(baseBody);

  const innerTray = new THREE.Group();
  innerTray.name = 'box-inner-tray';
  // The tray sits just proud of the base but remains behind the closed lid artwork.
  // This preserves true geometric occlusion instead of relying on a render-order trick.
  innerTray.position.z = 0.085;
  basePivot.add(innerTray);

  const trayFloor = new THREE.Mesh(
    new RoundedBoxGeometry(BOX_WIDTH - 0.38, BOX_HEIGHT - 0.38, 0.07, 4, 0.08),
    trayMaterial,
  );
  trayFloor.name = 'box-tray-floor';
  trayFloor.receiveShadow = true;
  innerTray.add(trayFloor);

  const trayRim = makePerimeterRim(
    BOX_WIDTH - 0.34,
    BOX_HEIGHT - 0.34,
    0.14,
    0.105,
    trayMaterial,
  );
  trayRim.position.z = 0.065;
  innerTray.add(trayRim);

  const cradle = new THREE.Group();
  cradle.name = 'box-cartridge-cradle';
  cradle.position.set(0, -0.13, 0.04);
  innerTray.add(cradle);

  const cradleBacking = new THREE.Mesh(
    new RoundedBoxGeometry(2.92, 2.02, 0.07, 4, 0.12),
    new THREE.MeshStandardMaterial({ color: 0x15161a, roughness: 0.9 }),
  );
  cradleBacking.name = 'box-cradle-backing';
  cradleBacking.receiveShadow = true;
  cradle.add(cradleBacking);

  const cradleRailGeometry = new RoundedBoxGeometry(0.16, 1.72, 0.09, 3, 0.035);
  for (const x of [-1.35, 1.35]) {
    const rail = new THREE.Mesh(cradleRailGeometry, trayMaterial);
    rail.position.set(x, 0, 0.025);
    rail.name = x < 0 ? 'box-cradle-left-rail' : 'box-cradle-right-rail';
    rail.castShadow = true;
    cradle.add(rail);
  }

  const thumbRecess = new THREE.Mesh(
    new THREE.TorusGeometry(0.27, 0.045, 8, 28, Math.PI),
    shellEdgeMaterial,
  );
  thumbRecess.name = 'box-cradle-thumb-recess';
  thumbRecess.rotation.z = Math.PI;
  thumbRecess.position.set(0, -0.98, 0.04);
  cradle.add(thumbRecess);

  const cartridgeCradleSocket = makeSocket(
    'box-cartridge-cradle-socket',
    new THREE.Vector3(0, -0.03, 0.21),
  );
  cradle.add(cartridgeCradleSocket);

  const cartridgeLandingSocket = makeSocket(
    'box-cartridge-landing-socket',
    new THREE.Vector3(0, -BOX_HEIGHT * 0.72, 1.05),
    new THREE.Euler(0, 0, THREE.MathUtils.degToRad(-4)),
  );
  root.add(cartridgeLandingSocket);

  const hingeSocket = makeSocket(
    'box-hinge-socket',
    new THREE.Vector3(0, BOX_HEIGHT * 0.5, BOX_DEPTH * 0.42),
  );
  root.add(hingeSocket);

  const baseHingeMaterial = new THREE.MeshPhysicalMaterial({
    color: 0x111216,
    roughness: 0.55,
    clearcoat: 0.12,
  });
  const hingeY = BOX_HEIGHT * 0.5 + 0.015;
  for (const [start, end] of [
    [-2.03, -0.82],
    [0.82, 2.03],
  ] as const) {
    const barrel = makeCylinderBetween(
      new THREE.Vector3(start, hingeY, BOX_DEPTH * 0.42),
      new THREE.Vector3(end, hingeY, BOX_DEPTH * 0.42),
      0.095,
      baseHingeMaterial,
      18,
    );
    barrel.name = 'box-base-hinge-barrel';
    basePivot.add(barrel);
  }

  const lidPivot = new THREE.Group();
  lidPivot.name = 'box-lid-pivot';
  lidPivot.position.copy(hingeSocket.position);
  root.add(lidPivot);

  const lidVisual = new THREE.Group();
  lidVisual.name = 'box-lid-visual';
  lidPivot.add(lidVisual);

  const lidBacking = new THREE.Mesh(
    new RoundedBoxGeometry(BOX_WIDTH, BOX_HEIGHT, 0.16, 5, 0.095),
    shellMaterial,
  );
  lidBacking.name = 'box-lid-shell';
  lidBacking.position.set(0, -BOX_HEIGHT * 0.5, 0.045);
  lidBacking.castShadow = true;
  lidBacking.receiveShadow = true;
  lidVisual.add(lidBacking);

  const lidInner = new THREE.Mesh(
    new RoundedBoxGeometry(BOX_WIDTH - 0.34, BOX_HEIGHT - 0.34, 0.052, 4, 0.07),
    trayMaterial,
  );
  lidInner.name = 'box-lid-inner-liner';
  lidInner.position.set(0, -BOX_HEIGHT * 0.5, -0.049);
  lidInner.receiveShadow = true;
  lidVisual.add(lidInner);

  // Molded manual pocket and retaining tabs keep the opened lid from reading as
  // an empty slab. These sit on the inside face and remain physically attached
  // through the same hinge animation as the lid shell.
  const manualPocket = makePerimeterRim(3.72, 2.88, 0.075, 0.11, shellEdgeMaterial);
  manualPocket.name = 'box-lid-manual-pocket-rim';
  manualPocket.position.set(0, -BOX_HEIGHT * 0.5, -0.094);
  lidVisual.add(manualPocket);
  const manualPanel = new THREE.Mesh(
    new RoundedBoxGeometry(3.42, 2.52, 0.035, 3, 0.06),
    new THREE.MeshStandardMaterial({ color: 0x26272e, roughness: 0.92 }),
  );
  manualPanel.name = 'box-lid-manual-recess';
  manualPanel.position.set(0, -BOX_HEIGHT * 0.5, -0.102);
  lidVisual.add(manualPanel);
  const lidClipGeometry = new RoundedBoxGeometry(0.22, 0.76, 0.1, 3, 0.035);
  for (const x of [-1.72, 1.72]) {
    const clip = new THREE.Mesh(lidClipGeometry, trayMaterial);
    clip.name = x < 0 ? 'box-lid-manual-clip-left' : 'box-lid-manual-clip-right';
    clip.position.set(x, -BOX_HEIGHT * 0.5, -0.13);
    lidVisual.add(clip);
  }

  const artwork = new THREE.Mesh(
    new THREE.PlaneGeometry(BOX_WIDTH - 0.14, BOX_HEIGHT - 0.17),
    artMaterial,
  );
  artwork.name = 'box-front-reference-artwork';
  artwork.position.set(0, -BOX_HEIGHT * 0.5, 0.13);
  artwork.castShadow = true;
  lidVisual.add(artwork);

  const punctureMaterial = new THREE.MeshStandardMaterial({
    color: 0x050506,
    roughness: 0.94,
    polygonOffset: true,
    polygonOffsetFactor: -2,
  });
  const puncture = new THREE.Mesh(new THREE.CircleGeometry(0.16, 28), punctureMaterial);
  puncture.name = 'box-artwork-puncture-depth';
  puncture.position.set(0.97, -BOX_HEIGHT * 0.5 - 1.49, 0.133);
  lidVisual.add(puncture);

  const punctureRim = new THREE.Mesh(
    new THREE.TorusGeometry(0.164, 0.012, 6, 28),
    new THREE.MeshStandardMaterial({ color: 0x7b756f, roughness: 0.88 }),
  );
  punctureRim.name = 'box-artwork-puncture-rim';
  punctureRim.position.copy(puncture.position).add(new THREE.Vector3(0, 0, 0.002));
  lidVisual.add(punctureRim);

  const filmMaterial = new THREE.MeshPhysicalMaterial({
    color: 0xffffff,
    transparent: true,
    opacity: 0.055,
    roughness: 0.1,
    metalness: 0,
    clearcoat: 1,
    clearcoatRoughness: 0.1,
    depthWrite: false,
    side: THREE.FrontSide,
  });
  filmMaterial.name = 'box-clear-cover-film';
  const film = new THREE.Mesh(
    new THREE.PlaneGeometry(BOX_WIDTH - 0.11, BOX_HEIGHT - 0.14),
    filmMaterial,
  );
  film.name = 'box-cover-film-overlay';
  film.position.set(0, -BOX_HEIGHT * 0.5, 0.138);
  film.renderOrder = 4;
  lidVisual.add(film);

  const scratches = makeBoxFilmScratches(BOX_WIDTH - 0.12, BOX_HEIGHT - 0.15);
  scratches.position.set(0, -BOX_HEIGHT * 0.5, 0.141);
  lidVisual.add(scratches);

  const lidBarrel = makeCylinderBetween(
    new THREE.Vector3(-0.73, 0, 0),
    new THREE.Vector3(0.73, 0, 0),
    0.1,
    baseHingeMaterial,
    18,
  );
  lidBarrel.name = 'box-lid-hinge-barrel';
  lidVisual.add(lidBarrel);

  const latchMaterial = new THREE.MeshStandardMaterial({ color: 0x101115, roughness: 0.74 });
  const latch = new THREE.Mesh(
    new RoundedBoxGeometry(0.58, 0.13, 0.13, 3, 0.03),
    latchMaterial,
  );
  latch.name = 'box-latch-catch';
  latch.position.set(0, -BOX_HEIGHT * 0.5 - 0.01, BOX_DEPTH * 0.5 + 0.015);
  basePivot.add(latch);

  let openProgress = 0;
  const setOpenProgress = (progress: number): void => {
    openProgress = clamp01(progress);
    const eased = easeInOut(openProgress);
    lidPivot.rotation.x = eased * BOX_LID_OPEN_ANGLE;
    latch.position.z = BOX_DEPTH * 0.5 + 0.015 - Math.sin(eased * Math.PI) * 0.025;
    root.userData.boxOpenProgress = openProgress;
    root.userData.boxState = openProgress <= 0.001 ? 'closed' : openProgress >= 0.999 ? 'open' : 'opening';
  };

  const setHover = (hovered: boolean): void => {
    shellMaterial.emissive.setHex(hovered ? 0x100d18 : 0x000000);
    shellMaterial.emissiveIntensity = hovered ? 0.32 : 0;
  };

  const colliders: BoxColliderDescriptor[] = [
    {
      id: 'box-base-solid',
      node: basePivot,
      type: 'box',
      size: new THREE.Vector3(BOX_WIDTH, BOX_HEIGHT, BOX_DEPTH),
      offset: new THREE.Vector3(),
      isTrigger: false,
    },
    {
      id: 'box-lid-solid',
      node: lidPivot,
      type: 'box',
      size: new THREE.Vector3(BOX_WIDTH, BOX_HEIGHT, 0.16),
      offset: new THREE.Vector3(0, -BOX_HEIGHT * 0.5, 0.045),
      isTrigger: false,
    },
    {
      id: 'box-cartridge-cradle-trigger',
      node: cartridgeCradleSocket,
      type: 'box',
      size: new THREE.Vector3(2.78, 1.88, 0.34),
      offset: new THREE.Vector3(),
      isTrigger: true,
    },
  ];

  const textureLoader = new THREE.TextureLoader();
  const ready = loadColorTexture(textureLoader, assetUrls.boxArtwork, artMaterial, textureAnisotropy);

  const runtime = {
    pivots: {
      root,
      base: basePivot,
      lid: lidPivot,
      tray: innerTray,
    },
    sockets: {
      hinge: hingeSocket,
      cartridgeCradle: cartridgeCradleSocket,
      cartridgeReveal: cartridgeCradleSocket,
      cartridgeLanding: cartridgeLandingSocket,
    },
    colliders,
    limits: {
      lidClosedAngle: 0,
      lidOpenAngle: BOX_LID_OPEN_ANGLE,
    },
    actions: {
      setOpenProgress,
      setHover,
    },
    evidence: {
      reference: publicAsset('references/game-box.png'),
      artworkCrop: assetUrls.boxArtwork,
      fidelity: 'front-faithful; depth, rear hinge and interior are conservative interaction-ready inferences',
    },
  };
  root.userData.sculptRuntime = runtime;
  setOpenProgress(0);

  return {
    root,
    basePivot,
    lidPivot,
    innerTray,
    hingeSocket,
    cartridgeCradleSocket,
    cartridgeLandingSocket,
    colliders,
    openAngle: BOX_LID_OPEN_ANGLE,
    ready,
    setOpenProgress,
    getOpenProgress: () => openProgress,
    setHover,
  };
}

function makeCartridgeShape(): THREE.Shape {
  const halfWidth = CARTRIDGE_WIDTH * 0.5;
  const halfHeight = CARTRIDGE_HEIGHT * 0.5;
  const shape = new THREE.Shape();

  shape.moveTo(-halfWidth + 0.13, -halfHeight);
  shape.quadraticCurveTo(-halfWidth, -halfHeight, -halfWidth, -halfHeight + 0.13);
  shape.lineTo(-halfWidth, halfHeight - 0.34);
  shape.lineTo(-halfWidth - 0.08, halfHeight - 0.34);
  shape.lineTo(-halfWidth - 0.08, halfHeight - 0.19);
  shape.lineTo(-halfWidth + 0.03, halfHeight - 0.19);
  shape.lineTo(-halfWidth + 0.03, halfHeight - 0.11);
  shape.quadraticCurveTo(-halfWidth + 0.04, halfHeight, -halfWidth + 0.2, halfHeight + 0.02);
  shape.quadraticCurveTo(0, halfHeight + 0.13, halfWidth - 0.2, halfHeight + 0.02);
  shape.quadraticCurveTo(halfWidth - 0.04, halfHeight, halfWidth - 0.03, halfHeight - 0.11);
  shape.lineTo(halfWidth - 0.03, halfHeight - 0.19);
  shape.lineTo(halfWidth + 0.08, halfHeight - 0.19);
  shape.lineTo(halfWidth + 0.08, halfHeight - 0.34);
  shape.lineTo(halfWidth, halfHeight - 0.34);
  shape.lineTo(halfWidth, -halfHeight + 0.13);
  shape.quadraticCurveTo(halfWidth, -halfHeight, halfWidth - 0.13, -halfHeight);
  shape.closePath();
  return shape;
}

function makeExtrudedCartridgeHalf(depth: number): THREE.ExtrudeGeometry {
  const geometry = new THREE.ExtrudeGeometry(makeCartridgeShape(), {
    depth,
    steps: 1,
    curveSegments: 24,
    bevelEnabled: true,
    bevelSegments: 3,
    bevelSize: 0.025,
    bevelThickness: 0.016,
  });
  geometry.translate(0, 0, -depth * 0.5);
  geometry.computeVertexNormals();
  return geometry;
}

function makeTopBrowGeometry(): THREE.ExtrudeGeometry {
  const shape = new THREE.Shape();
  shape.moveTo(-1.28, 0.72);
  shape.quadraticCurveTo(0, 0.92, 1.28, 0.72);
  shape.lineTo(1.2, 0.64);
  shape.quadraticCurveTo(0, 0.8, -1.2, 0.64);
  shape.closePath();
  const geometry = new THREE.ExtrudeGeometry(shape, {
    depth: 0.04,
    steps: 1,
    curveSegments: 24,
    bevelEnabled: true,
    bevelSegments: 2,
    bevelSize: 0.01,
    bevelThickness: 0.008,
  });
  geometry.translate(0, 0, -0.02);
  return geometry;
}

function createEmbossedWordmarkTexture(): THREE.CanvasTexture {
  const canvas = document.createElement('canvas');
  canvas.width = 1024;
  canvas.height = 128;
  const context = canvas.getContext('2d');
  if (context) {
    context.clearRect(0, 0, canvas.width, canvas.height);
    context.font = '700 italic 76px Arial Narrow, Arial, sans-serif';
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.fillStyle = 'rgba(8, 12, 12, 0.75)';
    context.fillText('GAME BOY ADVANCE', 512, 68);
    context.strokeStyle = 'rgba(92, 105, 101, 0.38)';
    context.lineWidth = 2;
    context.strokeText('GAME BOY ADVANCE', 512, 66);
  }
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.needsUpdate = true;
  return texture;
}

function createCartridge(
  assetUrls: GamePackageAssetUrls,
  textureAnisotropy: number,
): CartridgeModel {
  const root = new THREE.Group();
  root.name = 'game-cartridge-root';
  root.userData.interactionRole = 'cartridge';
  root.userData.pickable = true;

  const shellMaterial = new THREE.MeshPhysicalMaterial({
    color: 0x1a2221,
    roughness: 0.72,
    metalness: 0,
    clearcoat: 0.07,
    clearcoatRoughness: 0.4,
    emissive: 0x000000,
  });
  shellMaterial.name = 'cartridge-shell-plastic';

  const rearMaterial = shellMaterial.clone();
  rearMaterial.name = 'cartridge-rear-shell-plastic';
  rearMaterial.color.multiplyScalar(0.82);

  const labelMaterial = new THREE.MeshPhysicalMaterial({
    color: 0xffffff,
    roughness: 0.5,
    metalness: 0,
    clearcoat: 0.14,
    clearcoatRoughness: 0.34,
  });
  labelMaterial.name = 'cartridge-reference-label';

  const frontShell = new THREE.Group();
  frontShell.name = 'cartridge-front-shell';
  frontShell.position.z = CARTRIDGE_DEPTH * 0.25;
  root.add(frontShell);

  const frontMesh = new THREE.Mesh(
    makeExtrudedCartridgeHalf(CARTRIDGE_DEPTH * 0.5),
    shellMaterial,
  );
  frontMesh.name = 'cartridge-front-shell-mesh';
  frontMesh.castShadow = true;
  frontMesh.receiveShadow = true;
  frontShell.add(frontMesh);

  const rearShell = new THREE.Group();
  rearShell.name = 'cartridge-rear-shell';
  rearShell.position.z = -CARTRIDGE_DEPTH * 0.25;
  root.add(rearShell);

  const rearMesh = new THREE.Mesh(
    makeExtrudedCartridgeHalf(CARTRIDGE_DEPTH * 0.5),
    rearMaterial,
  );
  rearMesh.name = 'cartridge-rear-shell-mesh';
  rearMesh.castShadow = true;
  rearMesh.receiveShadow = true;
  rearShell.add(rearMesh);

  const brow = new THREE.Mesh(makeTopBrowGeometry(), shellMaterial);
  brow.name = 'cartridge-raised-top-brow';
  brow.position.z = CARTRIDGE_DEPTH * 0.5 + 0.012;
  brow.castShadow = true;
  root.add(brow);

  const labelRecess = new THREE.Mesh(
    new RoundedBoxGeometry(2.4, 1.34, 0.048, 4, 0.075),
    new THREE.MeshStandardMaterial({ color: 0x0d1211, roughness: 0.86 }),
  );
  labelRecess.name = 'cartridge-label-recess';
  labelRecess.position.set(0, -0.15, CARTRIDGE_DEPTH * 0.5 + 0.012);
  labelRecess.castShadow = true;
  root.add(labelRecess);

  const label = new THREE.Mesh(new THREE.PlaneGeometry(2.26, 1.23), labelMaterial);
  label.name = 'cartridge-reference-label';
  label.position.set(0, -0.15, CARTRIDGE_DEPTH * 0.5 + 0.041);
  label.castShadow = true;
  root.add(label);

  const wordmarkMaterial = new THREE.MeshBasicMaterial({
    map: createEmbossedWordmarkTexture(),
    transparent: true,
    depthWrite: false,
    toneMapped: false,
  });
  wordmarkMaterial.name = 'cartridge-embossed-wordmark-material';
  const wordmark = new THREE.Mesh(new THREE.PlaneGeometry(1.72, 0.215), wordmarkMaterial);
  wordmark.name = 'cartridge-embossed-wordmark';
  wordmark.position.set(0, 0.65, CARTRIDGE_DEPTH * 0.5 + 0.052);
  wordmark.renderOrder = 3;
  root.add(wordmark);

  const chevronMaterial = new THREE.MeshStandardMaterial({
    color: 0x3b4946,
    roughness: 0.62,
  });
  const chevron = new THREE.Group();
  chevron.name = 'cartridge-bottom-chevron';
  chevron.position.z = CARTRIDGE_DEPTH * 0.5 + 0.057;
  chevron.add(
    makeCylinderBetween(
      new THREE.Vector3(-0.34, -0.77, 0),
      new THREE.Vector3(0, -0.88, 0),
      0.012,
      chevronMaterial,
      8,
    ),
    makeCylinderBetween(
      new THREE.Vector3(0, -0.88, 0),
      new THREE.Vector3(0.34, -0.77, 0),
      0.012,
      chevronMaterial,
      8,
    ),
  );
  root.add(chevron);

  const contactMaterial = new THREE.MeshPhysicalMaterial({
    color: 0xc89a3a,
    roughness: 0.32,
    metalness: 0.9,
    clearcoat: 0.14,
  });
  contactMaterial.name = 'cartridge-contact-gold';
  const contactGeometry = new RoundedBoxGeometry(0.055, 0.31, 0.018, 2, 0.006);
  const contactCount = 28;
  const contacts = new THREE.InstancedMesh(contactGeometry, contactMaterial, contactCount);
  contacts.name = 'cartridge-contact-row';
  const contactMatrix = new THREE.Matrix4();
  const contactStart = -0.78;
  const contactSpacing = 1.56 / (contactCount - 1);
  for (let index = 0; index < contactCount; index += 1) {
    const x = contactStart + index * contactSpacing;
    contactMatrix.makeTranslation(x, -0.74, -CARTRIDGE_DEPTH * 0.5 - 0.012);
    contacts.setMatrixAt(index, contactMatrix);
  }
  contacts.instanceMatrix.needsUpdate = true;
  // The recessed contacts do not contribute a readable shadow. Keeping this
  // instanced detail out of the depth pass avoids a SwiftShader validation
  // failure while preserving the metallic front/rear rendering.
  contacts.castShadow = false;
  root.add(contacts);

  const rearSeamMaterial = new THREE.LineBasicMaterial({
    color: 0x080b0a,
    transparent: true,
    opacity: 0.72,
  });
  const seamGeometry = new THREE.EdgesGeometry(rearMesh.geometry, 35);
  const rearSeam = new THREE.LineSegments(seamGeometry, rearSeamMaterial);
  rearSeam.name = 'cartridge-shell-seam';
  rearShell.add(rearSeam);

  const outlineMaterial = new THREE.LineBasicMaterial({
    color: 0x8ddaff,
    transparent: true,
    opacity: 0.9,
    depthWrite: false,
  });
  const outline = new THREE.LineSegments(new THREE.EdgesGeometry(frontMesh.geometry, 28), outlineMaterial);
  outline.name = 'cartridge-selection-outline';
  outline.visible = false;
  outline.renderOrder = 8;
  frontShell.add(outline);

  const pickupSocket = makeSocket(
    'cartridge-pickup-grip-socket',
    new THREE.Vector3(0, 0, CARTRIDGE_DEPTH * 0.5 + 0.2),
  );
  root.add(pickupSocket);

  const insertionLeadSocket = makeSocket(
    'cartridge-insertion-lead-socket',
    new THREE.Vector3(0, -CARTRIDGE_HEIGHT * 0.5, 0),
  );
  root.add(insertionLeadSocket);

  const alignmentSocket = makeSocket(
    'cartridge-slot-alignment-socket',
    new THREE.Vector3(0, -CARTRIDGE_HEIGHT * 0.5 + 0.12, 0),
  );
  root.add(alignmentSocket);

  const contactEdgeSocket = makeSocket(
    'cartridge-contact-edge-socket',
    new THREE.Vector3(0, -CARTRIDGE_HEIGHT * 0.5 + 0.08, -CARTRIDGE_DEPTH * 0.5),
  );
  root.add(contactEdgeSocket);

  const insertionAxis = new THREE.Vector3(0, -1, 0);
  const colliders: BoxColliderDescriptor[] = [
    {
      id: 'cartridge-main-solid',
      node: root,
      type: 'box',
      size: new THREE.Vector3(2.64, 1.58, CARTRIDGE_DEPTH + 0.04),
      offset: new THREE.Vector3(0, -0.08, 0),
      isTrigger: false,
    },
    {
      id: 'cartridge-crown-solid',
      node: root,
      type: 'box',
      size: new THREE.Vector3(2.76, 0.34, CARTRIDGE_DEPTH + 0.04),
      offset: new THREE.Vector3(0, 0.72, 0),
      isTrigger: false,
    },
    {
      id: 'cartridge-insertion-trigger',
      node: insertionLeadSocket,
      type: 'box',
      size: new THREE.Vector3(2.48, 0.3, CARTRIDGE_DEPTH + 0.08),
      offset: new THREE.Vector3(0, 0.12, 0),
      isTrigger: true,
    },
  ];

  let interactionState: CartridgeInteractionState = 'idle';
  const setInteractionState = (state: CartridgeInteractionState): void => {
    interactionState = state;
    const highlighted = state === 'hovered' || state === 'selected';
    outline.visible = highlighted;
    outlineMaterial.color.setHex(state === 'selected' ? 0xffd66b : 0x8ddaff);
    outlineMaterial.opacity = state === 'selected' ? 1 : 0.78;
    shellMaterial.emissive.setHex(highlighted ? (state === 'selected' ? 0x261a05 : 0x071724) : 0x000000);
    shellMaterial.emissiveIntensity = highlighted ? 0.48 : 0;
    root.userData.cartridgeState = state;
    root.userData.inserted = state === 'inserted';
  };

  const textureLoader = new THREE.TextureLoader();
  const ready = loadColorTexture(
    textureLoader,
    assetUrls.cartridgeLabel,
    labelMaterial,
    textureAnisotropy,
  );

  const runtime = {
    pivots: {
      root,
      frontShell,
      rearShell,
    },
    sockets: {
      pickupGrip: pickupSocket,
      insertionLead: insertionLeadSocket,
      alignment: alignmentSocket,
      contactEdge: contactEdgeSocket,
    },
    colliders,
    insertion: {
      axis: insertionAxis,
      validLeadSocket: insertionLeadSocket,
      alignmentSocket,
      contactEdgeSocket,
      snapDistance: 0.36,
      orientationAssistDistance: 0.8,
      insertionDepth: 0.72,
    },
    actions: {
      setInteractionState,
    },
    evidence: {
      reference: publicAsset('references/cartridge.png'),
      labelCrop: assetUrls.cartridgeLabel,
      fidelity: 'front-faithful; back shell and contact edge are conservative standard-form inferences',
    },
  };
  root.userData.sculptRuntime = runtime;
  setInteractionState('idle');

  return {
    root,
    frontShell,
    rearShell,
    pickupSocket,
    insertionLeadSocket,
    alignmentSocket,
    contactEdgeSocket,
    insertionAxis,
    colliders,
    ready,
    setInteractionState,
    getInteractionState: () => interactionState,
  };
}

function socketPoseInParent(
  socket: THREE.Object3D,
  parent: THREE.Object3D,
): { position: THREE.Vector3; quaternion: THREE.Quaternion; scale: THREE.Vector3 } {
  parent.updateWorldMatrix(true, false);
  socket.updateWorldMatrix(true, false);
  const localMatrix = parent.matrixWorld.clone().invert().multiply(socket.matrixWorld);
  const position = new THREE.Vector3();
  const quaternion = new THREE.Quaternion();
  const scale = new THREE.Vector3();
  localMatrix.decompose(position, quaternion, scale);
  return { position, quaternion, scale };
}

export function createGamePackage(options: GamePackageOptions = {}): GamePackageModel {
  const assetUrls: GamePackageAssetUrls = {
    ...DEFAULT_ASSET_URLS,
    ...options.assetUrls,
  };
  const textureAnisotropy = options.textureAnisotropy ?? 8;
  const root = new THREE.Group();
  root.name = 'game-package-root';

  const box = createGameBox(assetUrls, textureAnisotropy);
  const cartridge = createCartridge(assetUrls, textureAnisotropy);
  root.add(box.root);

  const rawSetBoxOpenProgress = box.setOpenProgress;
  const setBoxOpenProgress = (progress: number): void => {
    rawSetBoxOpenProgress(progress);
    if (root.userData.cartridgeLocation === 'cradle') {
      cartridge.root.visible = box.getOpenProgress() > 0.035;
    }
  };
  box.setOpenProgress = setBoxOpenProgress;
  const boxRuntime = box.root.userData.sculptRuntime as {
    actions?: { setOpenProgress?: (progress: number) => void };
  };
  if (boxRuntime.actions) {
    boxRuntime.actions.setOpenProgress = setBoxOpenProgress;
  }

  const resetCartridgeToCradle = (): void => {
    box.cartridgeCradleSocket.add(cartridge.root);
    cartridge.root.position.set(0, 0, 0);
    cartridge.root.quaternion.identity();
    cartridge.root.scale.set(1, 1, 1);
    cartridge.setInteractionState('idle');
    cartridge.root.visible = box.getOpenProgress() > 0.035;
    root.userData.cartridgeRevealProgress = 0;
    root.userData.cartridgeLocation = 'cradle';
  };

  const dockCartridgeAt = (socket: THREE.Object3D): void => {
    socket.add(cartridge.root);
    cartridge.root.position.set(0, 0, 0);
    cartridge.root.quaternion.identity();
    cartridge.root.scale.set(1, 1, 1);
    cartridge.root.visible = true;
    root.userData.cartridgeLocation = 'docked';
  };

  const detachCartridgeTo = (parent: THREE.Object3D): void => {
    parent.attach(cartridge.root);
    cartridge.root.visible = true;
    root.userData.cartridgeLocation = 'free';
  };

  const setCartridgeRevealProgress = (progress: number): void => {
    const t = easeInOut(progress);
    if (cartridge.root.parent !== root) {
      root.attach(cartridge.root);
    }
    const start = socketPoseInParent(box.cartridgeCradleSocket, root);
    const end = socketPoseInParent(box.cartridgeLandingSocket, root);
    cartridge.root.position.lerpVectors(start.position, end.position, t);
    cartridge.root.position.y += Math.sin(t * Math.PI) * 0.52;
    cartridge.root.position.z += Math.sin(t * Math.PI) * 0.22;
    cartridge.root.quaternion.copy(start.quaternion).slerp(end.quaternion, t);
    cartridge.root.scale.lerpVectors(start.scale, end.scale, t);
    cartridge.root.visible = box.getOpenProgress() > 0.035 || t > 0.001;
    root.userData.cartridgeRevealProgress = clamp01(progress);
    root.userData.cartridgeLocation = t >= 0.999 ? 'landing' : t <= 0.001 ? 'cradle' : 'revealing';
  };

  resetCartridgeToCradle();

  const ready = Promise.all([box.ready, cartridge.ready]).then(() => undefined);
  const runtime = {
    pivots: {
      root,
      boxRoot: box.root,
      boxLid: box.lidPivot,
      cartridgeRoot: cartridge.root,
    },
    sockets: {
      cartridgeCradle: box.cartridgeCradleSocket,
      cartridgeLanding: box.cartridgeLandingSocket,
      cartridgePickup: cartridge.pickupSocket,
      cartridgeAlignment: cartridge.alignmentSocket,
      cartridgeInsertionLead: cartridge.insertionLeadSocket,
    },
    colliders: [...box.colliders, ...cartridge.colliders],
    actions: {
      setBoxOpenProgress,
      setCartridgeRevealProgress,
      resetCartridgeToCradle,
      detachCartridgeTo,
      dockCartridgeAt,
    },
  };
  root.userData.sculptRuntime = runtime;

  return {
    root,
    box,
    cartridge,
    ready,
    resetCartridgeToCradle,
    setCartridgeRevealProgress,
    detachCartridgeTo,
    dockCartridgeAt,
  };
}

export { createGameBox, createCartridge };
